import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useCreateAppointmentSlots,
  getGetLecturerSlotsQueryKey,
  useCancelAppointmentSlot,
  useConfirmAppointmentSlot,
  useRescheduleAppointmentSlot,
} from '../../../common/api/endpoints/appointments/appointments';
import { getGetUserMandatesQueryKey } from '../../../common/api/endpoints/users/users';
import { useAuthStore } from '../../../common/auth/store';
import { useApiErrorReporter } from '../../../common/utils/apiErrorReporter';
import { useDialog } from '../../../common/context/DialogContext';

/**
 * Custom hook for slot mutation operations
 */
export function buildCreateSlotPayload({
  formData,
  startTimeIso,
  endTimeIso,
  boundTaskIds,
  selectedKind,
}) {
  const payload = {
    startTime: startTimeIso,
    endTime: endTimeIso,
    boundTaskIds,
    eventKindId: formData.eventKindId,
  };

  if (selectedKind?.allowCustomCapacity && !selectedKind?.requiresTaskBinding) {
    payload.capacity = formData.capacity;
  }

  return payload;
}

export function buildReschedulePayload(formData) {
  const [hoursStr, minutesStr] = formData.startTime.split(':');
  const startTime = dayjs(formData.slotDate)
    .hour(parseInt(hoursStr, 10))
    .minute(parseInt(minutesStr, 10))
    .second(0)
    .millisecond(0);

  return { startTime: startTime.toISOString() };
}

export function getCompatibleBoundTaskIds(selectedTasks = []) {
  return Array.from(
    new Set(
      selectedTasks
        .map((task) => task?.id)
        .filter(Boolean),
    ),
  );
}

export const useEventCalendarMutations = (onSuccess) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user.id);
  const { reportApiError } = useApiErrorReporter();
  const { showToastMessage, showDialog } = useDialog();

  const invalidateGaps = async () => {
    // Invalidate slots and mandates to refresh planning gaps
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetLecturerSlotsQueryKey(undefined) }),
      queryClient.invalidateQueries({ queryKey: getGetUserMandatesQueryKey(userId) }),
    ]);
  };

  const createMutation = useCreateAppointmentSlots({
    mutation: {
      onSuccess: async () => {
        showToastMessage('success', t('lecturer.eventCalendar.sidebar.createSuccess'));
        await invalidateGaps();
        if (onSuccess) onSuccess();
      },
      onError: (err) => {
        reportApiError(err, t('lecturer.eventCalendar.sidebar.createTitle'));
      },
    },
  });

  const deleteMutation = useCancelAppointmentSlot({
    mutation: {
      onSuccess: async () => {
        await invalidateGaps();
        if (onSuccess) onSuccess();
        showToastMessage('success', t('lecturer.eventCalendar.sidebar.deleteSuccess'));
      },
      onError: (err) => reportApiError(err, 'Delete Slot'),
    },
  });

  const confirmSlotMutation = useConfirmAppointmentSlot({
    mutation: {
      onSuccess: async () => {
        await invalidateGaps();
        showToastMessage('success', t('lecturer.eventCalendar.sidebar.confirmSlotSuccess'));
      },
      onError: (err) => reportApiError(err, 'Confirm Slot'),
    },
  });

  const rescheduleMutation = useRescheduleAppointmentSlot({
    mutation: {
      onSuccess: async () => {
        showToastMessage('success', t('lecturer.eventCalendar.sidebar.rescheduleSuccess'));
        await invalidateGaps();
        if (onSuccess) onSuccess();
      },
      onError: (err) => reportApiError(err, t('lecturer.eventCalendar.sidebar.rescheduleErrorTitle')),
    },
  });

  const createSlot = (formData, selectedDate, selectedTasks, selectedKind) => {
    // Combine selected date with entered time (HH:mm)
    const [hoursStr, minutesStr] = formData.startTime.split(':');
    const startTime = dayjs(selectedDate)
      .hour(parseInt(hoursStr, 10))
      .minute(parseInt(minutesStr, 10))
      .second(0)
      .millisecond(0)
      .toDate();

    const endTime = new Date(startTime.getTime() + formData.durationMinutes * 60000);

    const boundTaskIds = getCompatibleBoundTaskIds(selectedTasks);

    const payload = buildCreateSlotPayload({
      formData,
      startTimeIso: startTime.toISOString(),
      endTimeIso: endTime.toISOString(),
      boundTaskIds,
      selectedKind,
    });

    createMutation.mutate({ data: payload });
  };

  const confirmDeleteSlot = (slot, activeBookingsCount) => {
    showDialog({
      title: t('common.buttons.delete'),
      message:
        activeBookingsCount > 0
          ? t('lecturer.eventCalendar.sidebar.deleteConfirmWithRegistrations', { count: activeBookingsCount })
          : t('lecturer.eventCalendar.sidebar.deleteConfirm'),
      buttons: [
        { text: t('lecturer.eventCalendar.sidebar.buttons.no_keep_it'), variant: 'outline' },
        {
          text: t('common.buttons.cancel'),
          variant: 'primary',
          action: 'negative',
          onPress: () => deleteMutation.mutate({ slotId: slot.slotId }),
        },
      ],
    });
  };

  const confirmSlotManually = (slot) => {
    if (!slot.slotId) return;
    confirmSlotMutation.mutate({ slotId: slot.slotId });
  };

  const confirmRescheduleSlot = (slot, payload, activeBookingsCount) => {
    showDialog({
      title: t('lecturer.eventCalendar.sidebar.rescheduleConfirmTitle'),
      message:
        activeBookingsCount > 0
          ? t('lecturer.eventCalendar.sidebar.rescheduleConfirmWithRegistrations', {
            count: activeBookingsCount,
          })
          : t('lecturer.eventCalendar.sidebar.rescheduleConfirm'),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline' },
        {
          text: t('lecturer.eventCalendar.sidebar.rescheduleButton'),
          variant: 'primary',
          onPress: () => rescheduleMutation.mutate({ slotId: slot.slotId, data: payload }),
        },
      ],
    });
  };

  const rescheduleSlot = (slot, formData, activeBookingsCount) => {
    const payload = buildReschedulePayload(formData);
    const currentStartIso = dayjs(slot.startTime).toISOString();
    if (payload.startTime === currentStartIso) {
      return;
    }
    confirmRescheduleSlot(slot, payload, activeBookingsCount);
  };

  return {
    createSlot,
    confirmDeleteSlot,
    createMutation,
    deleteMutation,
    confirmSlotManually,
    confirmSlotMutation,
    confirmRescheduleSlot,
    rescheduleSlot,
    rescheduleMutation,
  };
};
